/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Theme } from '@coursera/cds-core';
import { Grid, Typography2, VisuallyHidden, breakpoints, useTheme } from '@coursera/cds-core';

import _t from 'i18n!nls/program-home';

const progressBanner = (theme: Theme) =>
  css({
    margin: 'var(--cds-spacing-200) 0 var(--cds-spacing-400) 0',
    justifyContent: 'center',
    [breakpoints.down('sm')]: {
      minWidth: 0,
    },
    [breakpoints.down('xs')]: {
      minWidth: 0,
      margin: 'var(--cds-spacing-150) 0 var(--cds-spacing-400)',
    },
  });

const progressBar = (theme: Theme) =>
  css({
    width: '216px',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '10px',
    margin: 0,
    top: '160px',
    [breakpoints.down('xs')]: {
      maxWidth: '180px',
    },
  });

const greyBar = (theme: Theme) =>
  css({
    width: '100%',
    height: '10px',
    background: 'var(--cds-color-grey-50)',
    borderRadius: '10px',
  });

const fullProgressBar = (theme: Theme) =>
  css({
    width: '100%',
    height: '10px',
    background: 'var(--cds-color-blue-700)',
    borderRadius: '10px',
  });

const blueBar = (maxWidthAsPercentage: string, theme: Theme) =>
  css({
    height: '10px',
    background: 'var(--cds-color-blue-700)',
    display: 'block',
    borderRadius: '10px 0 0 10px',
    width: `${maxWidthAsPercentage}%`,
  });

export type ProgressBarProps = {
  progressPercentage: string;
  currentStep: number;
  totalSteps: number;
  currentStepName: string;
};

export const ProgressBar = ({ progressPercentage, currentStep, totalSteps, currentStepName }: ProgressBarProps) => {
  const theme = useTheme();

  return (
    <Grid container css={progressBanner(theme)} data-testid="progress-bar">
      <Grid>
        <Typography2 component="p" align="center">
          {_t(`Step #{currentStep} of #{totalSteps}: #{currentStepName}`, { currentStep, totalSteps, currentStepName })}
        </Typography2>
        <Grid item className="progress-bar" data-testid="onboarding-progress-bar" zeroMinWidth css={progressBar(theme)}>
          {progressPercentage !== '0' && (
            <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">{`${progressPercentage}% ${_t(
              'completed'
            )}`}</VisuallyHidden>
          )}
          <div css={greyBar(theme)}>
            <div css={progressPercentage === '100' ? fullProgressBar(theme) : blueBar(progressPercentage, theme)} />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
